import { Shield, Database, Eye, Lock, Users } from "lucide-react";
import LegalPage from "../Components/LegalPage";

const Section = ({ title, Icon, children }) => (
  <section className="mb-7 scroll-mt-24 last:mb-0 sm:mb-8">
    <h2 className="flex items-start gap-2.5 font-display text-[16px] font-bold leading-6 text-ink-800 sm:text-lg">
      {Icon && (
        <Icon size={17} className="mt-1 shrink-0 text-brand-700" />
      )}
      <span>{title}</span>
    </h2>
    <div className="mt-3 space-y-3.5 border-b border-sand-100 pb-6 text-[13px] leading-6 text-ink-600 sm:mt-4 sm:pb-7 sm:text-[14px] sm:leading-7">
      {children}
    </div>
  </section>
);

const Card = ({ title, children, tone }) => (
  <div className={tone === "warning" ? "border-l-2 border-rose-300 pl-4" : ""}>
    {title && (
      <h3 className={`mb-2 text-[13px] font-semibold ${tone === "warning" ? "text-rose-700" : "text-ink-800"}`}>
        {title}
      </h3>
    )}
    <div className="text-[12.5px] leading-5.5 text-ink-600 sm:text-[13px] sm:leading-6 [&_li]:pl-0.5 [&_strong]:font-semibold [&_strong]:text-ink-800 [&_ul]:marker:text-brand-500">
      {children}
    </div>
  </div>
);

const PrivacyPolicy = () => (
  <LegalPage
    title="Privacy Policy"
    subtitle="Your privacy is our priority. Learn how we protect and handle your personal information."
    Icon={Shield}
    lastUpdated="June 6, 2026"
  >
    <Section title="1. Who We Are" Icon={Shield}>
      <p>
        Karanki Matrimony is a matrimonial platform exclusively serving the Goud / Gowda community. We are based in Hyderabad, Telangana, India. This Privacy Policy explains how we collect, use, store and protect your personal data when you use our platform.
      </p>
    </Section>

    <Section title="2. Information We Collect" Icon={Database}>
      <div className="grid gap-3 sm:grid-cols-2">
        <Card title="Personal information">
          <ul className="list-disc space-y-1 pl-5">
            <li>Full name, date of birth, gender</li>
            <li>Mobile number and email address</li>
            <li>Caste, religion, mother tongue</li>
            <li>Education and professional background</li>
            <li>Family details and contact information</li>
            <li>Profile photographs</li>
            <li>Partner preferences</li>
          </ul>
        </Card>
        <Card title="Technical information">
          <ul className="list-disc space-y-1 pl-5">
            <li>IP address and device information</li>
            <li>Browser type and version</li>
            <li>Usage patterns and interactions</li>
            <li>Cookies and session data</li>
            <li>Payment transaction records</li>
          </ul>
        </Card>
      </div>
    </Section>

    <Section title="3. How We Use Your Information" Icon={Eye}>
      <div className="grid gap-3 sm:grid-cols-2">
        <Card title="Matchmaking services">
          <ul className="list-disc space-y-1 pl-5">
            <li>Create and display your matrimonial profile</li>
            <li>Suggest compatible matches</li>
            <li>Facilitate secure communication</li>
            <li>Show profile views and interest activity</li>
          </ul>
        </Card>
        <Card title="Platform safety">
          <ul className="list-disc space-y-1 pl-5">
            <li>Verify identities and prevent fraud</li>
            <li>Detect and suspend fake or misleading accounts</li>
            <li>Monitor for abuse and policy violations</li>
            <li>Comply with legal obligations</li>
          </ul>
        </Card>
      </div>
    </Section>

    <Section title="4. Fake Account Policy" Icon={Users}>
      <Card tone="warning" title="Zero tolerance for fake profiles">
        <p className="text-[13.5px] leading-6 text-rose-800">
          Karanki Matrimony maintains a strict zero-tolerance policy towards fake, misleading or fraudulent accounts. Any user found to have provided false information — including fake photos, incorrect identity, false marital status or impersonation — will have their account <strong>immediately suspended or permanently banned</strong> without prior notice or refund of any paid membership.
        </p>
        <ul className="mt-3 list-disc space-y-1 pl-5 text-[13.5px] text-rose-800">
          <li>All profiles are subject to admin verification</li>
          <li>We reserve the right to request identity proof at any time</li>
          <li>Reported profiles are reviewed and actioned promptly</li>
          <li>Legal action may be taken in cases of serious fraud or harassment</li>
        </ul>
      </Card>
    </Section>

    <Section title="5. Information Sharing" Icon={Users}>
      <p>
        We do not sell, trade or transfer your personal information to third parties without your consent, except as described below.
      </p>
      <Card>
        <ul className="list-disc space-y-1 pl-5">
          <li><strong>With potential matches:</strong> profile data shared for matchmaking within the platform.</li>
          <li><strong>Service providers:</strong> trusted partners (e.g., payment gateways, cloud hosting) who assist our operations under strict data agreements.</li>
          <li><strong>Legal requirements:</strong> when required by law, court order or to protect our rights.</li>
          <li><strong>With your consent:</strong> only when you explicitly agree.</li>
        </ul>
      </Card>
    </Section>

    <Section title="6. Data Security" Icon={Lock}>
      <div className="grid gap-3 sm:grid-cols-2">
        <Card title="Technical safeguards">
          <ul className="list-disc space-y-1 pl-5">
            <li>Encrypted data storage and transmission</li>
            <li>Secure authentication via Firebase</li>
            <li>Regular security reviews</li>
            <li>Payment data handled by Razorpay (PCI-DSS compliant)</li>
          </ul>
        </Card>
        <Card title="Access controls">
          <ul className="list-disc space-y-1 pl-5">
            <li>Limited admin access to personal data</li>
            <li>Role-based access within the platform</li>
            <li>Incident response procedures</li>
            <li>Data minimisation principles applied</li>
          </ul>
        </Card>
      </div>
    </Section>

    <Section title="7. Your Rights">
      <div className="grid gap-3 sm:grid-cols-2">
        <Card>
          <ul className="list-disc space-y-1 pl-5">
            <li><strong>Access:</strong> request a copy of your data.</li>
            <li><strong>Correction:</strong> update or correct inaccurate information.</li>
            <li><strong>Deletion:</strong> request deletion of your account and data.</li>
            <li><strong>Portability:</strong> receive your data in a structured format.</li>
          </ul>
        </Card>
        <Card>
          <ul className="list-disc space-y-1 pl-5">
            <li><strong>Restriction:</strong> limit how we process your data.</li>
            <li><strong>Objection:</strong> object to certain types of processing.</li>
            <li><strong>Withdraw consent:</strong> revoke permissions at any time.</li>
            <li><strong>Complaint:</strong> raise concerns with data protection authorities.</li>
          </ul>
        </Card>
      </div>
    </Section>

    <Section title="8. Cookies">
      <p>
        We use cookies and similar technologies to maintain your session, remember your preferences and improve platform performance. You may disable cookies in your browser settings, though some features may not work correctly as a result.
      </p>
    </Section>

    <Section title="9. Changes to This Policy">
      <p>
        We may update this Privacy Policy periodically. We will notify you of significant changes via email or an in-app notice. Continued use of the platform after updates constitutes acceptance of the revised policy.
      </p>
    </Section>

    <Section title="10. Contact Us">
      <p>
        If you have questions about this Privacy Policy or wish to exercise your rights, please contact us via the Contact Us page.
      </p>
    </Section>
  </LegalPage>
);

export default PrivacyPolicy;
